import { CreativeEditorSection } from "./CreativeEditorDialog";
import { Palette, Plus, X } from "@digital-twins/design-system/icons";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Text } from "@/components/ui/Text";
import type { BrandPaletteEditorProps } from "@/types/creative-library";

export function BrandPaletteEditor({
  colors,
  onChange,
}: BrandPaletteEditorProps) {
  const { t } = useTranslation("app");
  return (
    <CreativeEditorSection>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Palette
            className="h-4 w-4 text-muted-foreground"
            aria-hidden="true"
          />
          <Label>{t("creativeLibrary.palette")}</Label>
        </div>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="gap-1"
          onClick={() => onChange([...colors, { name: "", value: "#000000" }])}
          disabled={colors.length >= 20}
          data-testid="creative-brand-add-color"
        >
          <Plus className="h-4 w-4" />
          {t("creativeLibrary.addColor")}
        </Button>
      </div>
      {colors.length === 0 && (
        <Text size="xs" color="muted">
          {t("creativeLibrary.paletteHelp")}
        </Text>
      )}
      {colors.map((color, index) => (
        <div key={index} className="flex items-center gap-2">
          <Input
            aria-label={t("creativeLibrary.colorValue")}
            type="color"
            className="h-11 w-11 shrink-0 cursor-pointer rounded-lg p-1"
            value={color.value}
            onChange={(e) =>
              onChange(
                colors.map((c, i) =>
                  i === index ? { ...c, value: e.target.value } : c,
                ),
              )
            }
          />
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <Input
              aria-label={t("creativeLibrary.colorName")}
              maxLength={80}
              value={color.name}
              onChange={(e) =>
                onChange(
                  colors.map((c, i) =>
                    i === index ? { ...c, name: e.target.value } : c,
                  ),
                )
              }
              placeholder={t("creativeLibrary.colorName")}
            />
            <Text size="xs" color="muted" className="font-mono">
              {color.value.toUpperCase()}
            </Text>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => onChange(colors.filter((_, i) => i !== index))}
            aria-label={t("creativeLibrary.remove")}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ))}
    </CreativeEditorSection>
  );
}
